import { db } from '../firebase/config';
import {
  collection,
  query,
  where,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { useState, useEffect, useCallback } from 'react';
import { getKoreanToday } from '../utils/dateUtils';
import { calculateTotalReadPages } from '../utils/bookUtils';
import { getPageScale } from '../utils/pageScale';

export const useBooks = (userId) => {
  const [books, setBooks] = useState([]);
  const [completedBooks, setCompletedBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setBooks([]);
      setCompletedBooks([]);
      setLoading(false);
      return;
    }

    // 현재 읽고 있는 책들 구독
    const booksQuery = query(
      collection(db, 'books'),
      where('userId', '==', userId)
    );

    // 완독한 책들 구독
    const completedBooksQuery = query(
      collection(db, 'completedBooks'),
      where('userId', '==', userId)
    );

    const unsubscribeBooks = onSnapshot(booksQuery, (snapshot) => {
      const booksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBooks(booksData);
      setLoading(false);
    });

    const unsubscribeCompletedBooks = onSnapshot(completedBooksQuery, (snapshot) => {
      const completedBooksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCompletedBooks(completedBooksData);
    });

    return () => {
      unsubscribeBooks();
      unsubscribeCompletedBooks();
    };
  }, [userId]);

  // useCallback으로 래핑하여 메모이제이션 활성화
  const calculateTodayStats = useCallback((today) => {
    const stats = {
      pages: 0,
      minutes: 0,
      booksRead: 0
    };

    // 쪽수는 사용자가 입력·확인하는 단위로 센다 (전자책이면 전자책 쪽).
    // 시간은 minutesPerPage 가 그 단위 기준으로 잡혀 있으므로 같은 값을 쓴다.
    const add = (book) => {
      const todayPages = getPageScale(book).toDisplay(book.readingHistory?.[today] || 0) ?? 0;
      if (todayPages > 0) {
        stats.pages += todayPages;
        stats.minutes += todayPages * (book.minutesPerPage || 0);
        stats.booksRead += 1;
      }
    };

    books.forEach(add);
    completedBooks.forEach(add);

    return stats;
  }, [books, completedBooks]);

  const deleteBook = async (bookId, keepReadingHistory = false) => {
    try {
      const bookRef = doc(db, 'books', bookId);
      const bookDoc = await getDoc(bookRef);
      const bookData = bookDoc.data();

      if (keepReadingHistory && bookData) {
        // completedBooks 컬렉션에 이미 존재하는지 확인 (startDate 추가로 더 정확한 비교)
        const existingCompletedBook = completedBooks.find(cb =>
          cb.title === bookData.title &&
          cb.author === bookData.author &&
          cb.totalPages === bookData.totalPages &&
          cb.startDate === bookData.startDate
        );

        // 이미 completedBooks에 없는 경우에만 추가
        if (!existingCompletedBook) {
          const completedBookRef = collection(db, 'completedBooks');
          await addDoc(completedBookRef, {
            ...bookData,
            completedAt: new Date().toISOString(),
            completedDate: getKoreanToday(),
            status: 'completed',
            currentPage: bookData.totalPages,
            progress: 100
          });
        }
      }

      // books 컬렉션에서 책 삭제
      await deleteDoc(bookRef);

    } catch (error) {
      console.error('Error deleting book:', error);
      throw error;
    }
  };

  const updateBook = async (bookId, updatedData) => {
    try {
      const bookRef = doc(db, 'books', bookId);
      const book = books.find(b => b.id === bookId);
      if (!book) throw new Error('Book not found');

      if (updatedData.memos) {
        await updateDoc(bookRef, {
          ...updatedData,
          updatedAt: serverTimestamp()
        });
      } else {
        // 변경분을 반영한 뒤에 계산해야 한다.
        // 예전에는 항상 기존 book 기준으로 계산해서, totalPages 나 readingHistory 를
        // 같이 바꾸면 진행률이 옛 값으로 저장됐다.
        const merged = { ...book, ...updatedData };
        const totalReadPages = calculateTotalReadPages(merged);
        const progress = merged.totalPages > 0
          ? Math.round((totalReadPages / merged.totalPages) * 100)
          : 0;

        await updateDoc(bookRef, {
          ...updatedData,
          progress,
          currentPage: totalReadPages,
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Failed to update book:', error);
      throw error;
    }
  };

  const updateReadingProgress = async (bookId, newPages, onReadingComplete) => {
    try {
      const book = books.find(b => b.id === bookId);
      if (!book) throw new Error('Book not found');

      // 공통 유틸 함수 사용
      const today = getKoreanToday();

      const bookRef = doc(db, 'books', bookId);

      const currentDailyPages = book.readingHistory?.[today] || 0;
      const updatedReadingHistory = {
        ...book.readingHistory,
        [today]: currentDailyPages + newPages
      };

      // 업데이트된 히스토리로 총 읽은 페이지 계산
      const historySum = Object.values(updatedReadingHistory).reduce((sum, pages) => sum + pages, 0);
      const totalReadPages = historySum + (book.initialPage || 0);

      if (totalReadPages > book.totalPages) {
        throw new Error('전체 페이지 수를 초과할 수 없습니다.');
      }

      const batch = writeBatch(db);

      batch.update(bookRef, {
        readingHistory: updatedReadingHistory,
        currentPage: totalReadPages,
        progress: Math.round((totalReadPages / book.totalPages) * 100),
        lastReadAt: serverTimestamp()
      });

      await batch.commit();

      // 누적일 업데이트를 위해 handleReadingComplete 호출
      if (onReadingComplete) {
        await onReadingComplete(book.title);
      }

      // 업데이트된 책 정보 반환 (race condition 방지용)
      return {
        bookId,
        today,
        updatedReadingHistory
      };
    } catch (error) {
      console.error('Failed to update reading progress:', error);
      throw error;
    }
  };

  /**
   * 분야만 바꾼다 (books 컬렉션의 책).
   * updateBook 은 진행률을 다시 계산해 덮어쓰는데, 분야를 고칠 때는 그럴 이유가 없다.
   * 예전 완독 책(completedBooks)은 읽기 전용이라 여기로 오지 않는다 —
   * AppDataContext 가 사용자 문서에 따로 적는다.
   */
  const setBookKdc = useCallback(async (bookId, { kdc, kdcDivision, kdcClassNo, kdcSource }, collectionName = 'books') => {
    await updateDoc(doc(db, collectionName, bookId), {
      kdc: kdc || null,
      // 중분류는 주류와 짝이 맞을 때만 남긴다 (주류를 바꾸면 옛 중분류는 버린다)
      kdcDivision: kdc && kdcDivision && kdcDivision[0] === String(kdc) ? kdcDivision : null,
      // 도서관이 붙인 분류번호 원본. 직접 고르면 더는 그 번호가 아니므로 지운다
      kdcClassNo: kdc && kdcSource !== 'manual' ? (kdcClassNo || null) : null,
      // 자동으로 찾다 실패한 책은 'notfound' 로 남겨, 다음에 또 찾으러 가지 않게 한다.
      // (국중도에 없는 영어 원서를 버튼 누를 때마다 다시 조회하게 된다)
      kdcSource: kdc ? (kdcSource || 'manual') : (kdcSource === 'notfound' ? 'notfound' : null),
      updatedAt: serverTimestamp()
    });
  }, []);

  return {
    books,
    completedBooks,
    loading,
    calculateTodayStats,
    setBookKdc,
    addBook: async (bookData) => {
      try {
        const initialPage = bookData.initialPage || 0;
        await addDoc(collection(db, 'books'), {
          ...bookData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          progress: Math.round((initialPage / bookData.totalPages) * 100),
          currentPage: initialPage,
          readingHistory: {},
          imageUrl: bookData.imageUrl || ''
        });
      } catch (error) {
        console.error('Failed to add book:', error);
        throw error;
      }
    },
    updateBook,
    deleteBook,
    updateReadingProgress
  };
};
